import { FC, PropsWithChildren, Ref, ReactNode, forwardRef, useState, useEffect } from 'react';
import { BUTTON_SIZES } from 'ui/button';
import { PositiveFill } from 'ui/icons/status/PositiveFill';
import { WarningFill16 } from 'ui/icons/aurora/16px/WarningFill16';
import { Popover, PopoverContent, PopoverTrigger } from 'ui/popover';
import { ButtonNoVariantProps } from 'ui/button/types';
import { Skeleton } from 'ui/skeleton/Skeleton';
import {
  PREVIEW_USERS_DISABLED_HINT,
  PREVIEW_USERS_ERROR_BODY,
  PREVIEW_USERS_ERROR_LEAD,
  PREVIEW_USERS_MET_CRITERIA,
  TEST_IDS,
} from 'modules/predictiveGoals/constants';
import { BorderRadiusSkeleton } from 'modules/predictiveGoals/components/Styled';
import { TopTooltip } from 'ui/tooltip/Tooltip';
import {
  countMeetsRequirement,
  interpretCountStyle,
  getPreviewUsersLeadingText,
  getPreviewUsersCountText,
} from './previewUtils';
import {
  ApproxUserCount,
  Body2Bold,
  PreviewButton,
  PreviewUsersExtendedLoadingContainer,
  PreviewUsersExtendedLoadingSubContainer,
  StyledMultipleUsers,
  UserPreviewContentContainer,
  UserPreviewTextContainer,
} from './PreviewUsers.styles';

export const ForwardPreviewButton = forwardRef<HTMLButtonElement, ButtonNoVariantProps>(
  (props, forwardedRef: Ref<HTMLButtonElement>) => {
    return (
      <PreviewButton {...props} ref={forwardedRef}>
        {props.children}
      </PreviewButton>
    );
  },
);

export const UserPreviewIcon = ({ usersCount }: { usersCount: number }) => {
  const meetsReq = countMeetsRequirement(usersCount);
  const ResultIcon = meetsReq ? PositiveFill : WarningFill16;
  const resultColor = interpretCountStyle(meetsReq);

  return (
    <ResultIcon
      color={resultColor}
      width={32}
      height={32}
      data-test={`user-preview-${meetsReq ? 'pass' : 'fail'}`}
    />
  );
};

export const PREVIEW_BUTTON_ID = 'preview-users-id';
export const PREVIEW_ERROR_ID = 'preview-users-error';
export const PREVIEW_EXTENDED_LOADING = 'preview-users-extending-loading';
export interface PreviewUsersProps {
  children?: ReactNode;
  displayData?: boolean;
  isDisabled?: boolean;
  isError?: boolean;
  isExtendedLoading?: boolean;
  isLoading?: boolean;
  onClickPreview: () => void;
  usersCount: number;
}
export const PreviewUsers: FC<PropsWithChildren<PreviewUsersProps>> = ({
  displayData = false,
  isError = false,
  isExtendedLoading = false,
  isLoading = false,
  isDisabled = false,
  onClickPreview,
  usersCount,
}) => {
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const LEADING_TEXT = getPreviewUsersLeadingText(usersCount);
  const FORMATTED_COLORED_USER_TEXT = getPreviewUsersCountText(usersCount);

  const onSetPopover = (open: boolean) => {
    // Displaying the content controlled externally, so return if popover attempting to open
    if (open) {
      return;
    }
    // Allow all other events to close popover
    setPopoverOpen(open);
  };

  const handlePreviewClick = () => {
    setPopoverOpen(false);
    onClickPreview();
  };
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (isExtendedLoading) {
      setPopoverOpen(true);
      return;
    }
    // Ignore same values
    if (displayData === isPopoverOpen) {
      return;
    }
    setPopoverOpen(displayData);
  }, [displayData, isExtendedLoading]);

  let content = null;
  if (isExtendedLoading) {
    content = (
      <PreviewUsersExtendedLoadingContainer data-test={PREVIEW_EXTENDED_LOADING}>
        <Skeleton width={20} height={30} isRound shimmerSpeed={2000} />
        <PreviewUsersExtendedLoadingSubContainer>
          <BorderRadiusSkeleton width={300} height={20} />
          <BorderRadiusSkeleton width={300} height={20} />
        </PreviewUsersExtendedLoadingSubContainer>
      </PreviewUsersExtendedLoadingContainer>
    );
  } else {
    content = isError ? (
      <UserPreviewContentContainer>
        <UserPreviewTextContainer data-test={PREVIEW_ERROR_ID}>
          <Body2Bold>{PREVIEW_USERS_ERROR_LEAD}</Body2Bold> {PREVIEW_USERS_ERROR_BODY}
        </UserPreviewTextContainer>
      </UserPreviewContentContainer>
    ) : (
      <UserPreviewContentContainer data-test={TEST_IDS.PreviewUsers}>
        <UserPreviewIcon usersCount={usersCount} />
        <UserPreviewTextContainer>
          {LEADING_TEXT}
          <ApproxUserCount $color={interpretCountStyle(countMeetsRequirement(usersCount))}>
            {` ${FORMATTED_COLORED_USER_TEXT} `}
          </ApproxUserCount>
          {PREVIEW_USERS_MET_CRITERIA}
        </UserPreviewTextContainer>
      </UserPreviewContentContainer>
    );
  }

  return (
    <Popover open={isPopoverOpen} onOpenChange={onSetPopover}>
      <PopoverTrigger>
        <TopTooltip isEnabled={isDisabled} tooltipContent={PREVIEW_USERS_DISABLED_HINT}>
          <ForwardPreviewButton
            size={BUTTON_SIZES.MEDIUM}
            isLoading={isLoading}
            onClick={handlePreviewClick}
            data-test={PREVIEW_BUTTON_ID}
            isDisabled={isDisabled}
          >
            <StyledMultipleUsers />
            Preview users
          </ForwardPreviewButton>
        </TopTooltip>
      </PopoverTrigger>
      <PopoverContent align="end">{content}</PopoverContent>
    </Popover>
  );
};
